import React, { useState, useEffect } from 'react';
import { ArrowUp, ArrowDown, Users, TrendingUp, Activity, Target, BarChart3, PieChart } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API_URL}/api/analytics/engagement`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1C2127] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2DD4BF] mx-auto mb-4"></div>
          <p className="text-[#F8F9FA]">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#1C2127] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="px-6 py-2 bg-[#2DD4BF] text-[#1C2127] rounded-lg font-semibold hover:bg-[#26B8A6] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  const { overview, traffic_sources, onboarding_funnel, user_engagement } = analytics;

  return (
    <div className="min-h-screen bg-[#1C2127] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#F8F9FA] mb-2">Analytics Dashboard</h1>
          <p className="text-[#B0B8C1]">Beta testing metrics and user insights</p>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<Users className="w-6 h-6" />}
            title="Total Signups"
            value={overview.total_users}
            color="bg-[#2DD4BF]"
          />
          <StatCard
            icon={<Target className="w-6 h-6" />}
            title="Completed Onboarding"
            value={overview.onboarded_users}
            subtitle={`${overview.onboarding_completion_rate}% completion`}
            color="bg-[#D4AF37]"
          />
          <StatCard
            icon={<Activity className="w-6 h-6" />}
            title="Active This Week"
            value={overview.active_users_week}
            color="bg-[#2DD4BF]"
          />
          <StatCard
            icon={<TrendingUp className="w-6 h-6" />}
            title="Returning Users"
            value={overview.returning_users}
            color="bg-[#D4AF37]"
          />
        </div>

        {/* Traffic Sources */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-[#2D3640] rounded-xl p-6 border border-[#3D4752]">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-[#2DD4BF]/10 rounded-lg">
                <PieChart className="w-5 h-5 text-[#2DD4BF]" />
              </div>
              <h2 className="text-xl font-semibold text-[#F8F9FA]">Traffic Sources</h2>
            </div>
            <div className="space-y-3">
              {Object.entries(traffic_sources).map(([source, count]) => {
                const percentage = ((count / overview.total_users) * 100).toFixed(1);
                return (
                  <div key={source} className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-[#F8F9FA] font-medium capitalize min-w-[120px]">
                        {source.replace('_', ' ')}
                      </span>
                      <div className="flex-1 bg-[#1C2127] rounded-full h-2">
                        <div
                          className="bg-[#2DD4BF] h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <span className="text-[#B0B8C1] ml-4">
                      {count} ({percentage}%)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* User Engagement */}
          <div className="bg-[#2D3640] rounded-xl p-6 border border-[#3D4752]">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-[#D4AF37]/10 rounded-lg">
                <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <h2 className="text-xl font-semibold text-[#F8F9FA]">User Engagement</h2>
            </div>
            <p className="text-sm text-[#B0B8C1] mb-4">
              Of {user_engagement.total_onboarded} users who completed onboarding:
            </p>
            <div className="space-y-4">
              <EngagementMetric
                label="Opened Module 1"
                count={user_engagement.opened_module_1}
                total={user_engagement.total_onboarded}
                percentage={user_engagement.module_1_rate}
              />
              <EngagementMetric
                label="Completed a Check-in"
                count={user_engagement.completed_checkin}
                total={user_engagement.total_onboarded}
                percentage={user_engagement.checkin_rate}
              />
              <EngagementMetric
                label="Logged in Multiple Times"
                count={user_engagement.multiple_logins}
                total={user_engagement.total_onboarded}
                percentage={user_engagement.retention_rate}
              />
            </div>
          </div>
        </div>

        {/* Onboarding Funnel */}
        <div className="bg-[#2D3640] rounded-xl p-6 border border-[#3D4752]">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-[#2DD4BF]/10 rounded-lg">
              <Target className="w-5 h-5 text-[#2DD4BF]" />
            </div>
            <h2 className="text-xl font-semibold text-[#F8F9FA]">Onboarding Drop-off Analysis</h2>
          </div>
          
          <div className="space-y-3">
            <FunnelStep
              step="Step 1: Set Intentions"
              count={onboarding_funnel.step_1_intentions}
              percentage={100}
              isFirst
            />
            <FunnelStep
              step="Step 2: Reasons for Using"
              count={onboarding_funnel.step_2_reasons}
              percentage={(onboarding_funnel.step_2_reasons / overview.total_users * 100).toFixed(1)}
            />
            <FunnelStep
              step="Step 3: Timeline & Impact"
              count={onboarding_funnel.step_3_timeline}
              percentage={(onboarding_funnel.step_3_timeline / overview.total_users * 100).toFixed(1)}
            />
            <FunnelStep
              step="Step 4: Previous Attempts"
              count={onboarding_funnel.step_4_tried_before}
              percentage={(onboarding_funnel.step_4_tried_before / overview.total_users * 100).toFixed(1)}
            />
            <FunnelStep
              step="Step 5: Completed"
              count={onboarding_funnel.step_5_completed}
              percentage={onboarding_funnel.completion_rate}
              isLast
            />
          </div>

          <div className="mt-6 p-4 bg-[#1C2127] rounded-lg border border-[#3D4752]">
            <div className="flex items-center justify-between">
              <span className="text-[#B0B8C1]">Total Drop-off Rate</span>
              <span className="text-2xl font-bold text-red-400">
                {onboarding_funnel.drop_off_rate}%
              </span>
            </div>
            <p className="text-sm text-[#B0B8C1] mt-2">
              {overview.total_users - overview.onboarded_users} users didn't complete onboarding
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, title, value, subtitle, color }) {
  return (
    <div className="bg-[#2D3640] rounded-xl p-6 border border-[#3D4752]">
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 ${color}/10 rounded-lg`}>
          <div className={`${color.replace('bg-', 'text-')}`}>
            {icon}
          </div>
        </div>
      </div>
      <h3 className="text-[#B0B8C1] text-sm mb-2">{title}</h3>
      <p className="text-3xl font-bold text-[#F8F9FA] mb-1">{value}</p>
      {subtitle && <p className="text-sm text-[#2DD4BF]">{subtitle}</p>}
    </div>
  );
}

function EngagementMetric({ label, count, total, percentage }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[#F8F9FA] font-medium">{label}</span>
        <span className="text-[#2DD4BF] font-semibold">{percentage}%</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1 bg-[#1C2127] rounded-full h-2">
          <div
            className="bg-[#2DD4BF] h-2 rounded-full transition-all"
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
        <span className="text-sm text-[#B0B8C1] min-w-[60px] text-right">
          {count}/{total}
        </span>
      </div>
    </div>
  );
}

function FunnelStep({ step, count, percentage, isFirst, isLast }) {
  return (
    <div className="relative">
      <div className="flex items-center gap-4">
        <div className="flex-1 bg-[#1C2127] rounded-lg p-4 border border-[#3D4752]">
          <div className="flex items-center justify-between">
            <span className="text-[#F8F9FA] font-medium">{step}</span>
            <div className="flex items-center gap-3">
              <span className="text-[#B0B8C1]">{count} users</span>
              <span className={`font-semibold ${isLast ? 'text-[#2DD4BF]' : 'text-[#F8F9FA]'}`}>
                {percentage}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
